import {
  mapIndustrySelectionFromApi,
  mapIndustrySelectionToApi,
} from '../../shared/industries/industry-applicability.mapper';
import {
  catalogUserOId,
  momentumUserSnapshotFromApi,
} from '../../shared/models/momentum-user.models';
import type {
  AssetDetailResponseDto,
  AssetVersionDetailResponseDto,
  ProjectLinkResponseDto,
  ProjectPickerItemDto,
  UpdateAssetVersionRequestBody,
} from './models/asset-api.models';
import { normalizeIsoCalendarDate } from '../../shared/utils/iso-calendar-date.util';
import { normalizeTaxonomyFilterValues } from './asset-taxonomy.util';
import {
  resolveAssetReviewDate,
  type AssetReviewFormValue,
} from './models/asset-review-form.models';

function mapProjectLinkToPickerItem(link: ProjectLinkResponseDto): ProjectPickerItemDto {
  const title = link.title?.trim() || link.cNumber;
  return {
    cNumber: link.cNumber,
    title,
    referenceAssetId: link.status === 'HeldByOtherReference' ? (link.heldByAssetId ?? null) : null,
  };
}

export function mapAssetVersionToReviewForm(
  version: AssetVersionDetailResponseDto,
): AssetReviewFormValue {
  const firstProject = version.projects?.[0];
  return {
    title: version.title,
    description: version.description ?? '',
    type: version.type,
    industries: mapIndustrySelectionFromApi(version.industries),
    practices: [...(version.practices ?? [])],
    tags: [...(version.tags ?? [])],
    owner: momentumUserSnapshotFromApi(version.owner),
    confidentiality: version.confidentiality,
    reviewDate: resolveAssetReviewDate(normalizeIsoCalendarDate(version.reviewDate)),
    language: version.contentLanguage ?? '',
    relatedCapability: '',
    linkedProject: firstProject ? mapProjectLinkToPickerItem(firstProject) : null,
  };
}

export function mapDetailToReviewForm(detail: AssetDetailResponseDto): AssetReviewFormValue {
  return mapAssetVersionToReviewForm(detail.currentVersion);
}

export function mapReviewFormToUpdateBody(
  form: AssetReviewFormValue,
  changeReason?: string | null,
): UpdateAssetVersionRequestBody {
  const body: UpdateAssetVersionRequestBody = {
    title: form.title.trim(),
    description: form.description.trim() || null,
    type: form.type,
    industries: mapIndustrySelectionToApi(form.industries),
    practices: normalizeTaxonomyFilterValues(form.practices),
    tags: form.tags,
    contentLanguage: form.language.trim() || null,
    ownerOId: catalogUserOId(form.owner) ?? '',
    confidentiality: form.confidentiality,
    reviewDate: `${resolveAssetReviewDate(form.reviewDate)}T00:00:00.000Z`,
    isGolden: false,
    ...(changeReason !== undefined
      ? {
          changeReason:
            typeof changeReason === 'string' && changeReason.trim() ? changeReason.trim() : null,
        }
      : {}),
  };

  if (form.type === 'Reference') {
    const cNumber = form.linkedProject?.cNumber?.trim();
    if (cNumber) {
      body.projectCNumbers = [cNumber];
    }
  }

  return body;
}

export function titleFromFileName(fileName: string): string {
  const lastDot = fileName.lastIndexOf('.');
  return lastDot > 0 ? fileName.slice(0, lastDot) : fileName;
}
